/** Display order for Pump / Ceiling Fan mismatch tables (one table per field). */
export const PUMP_FIELD_ORDER = [
  'Watt',
  'Watt/Area',
  'Design Level Calc Method',
  'Frac. Latent',
  'Frac. Lost',
  'Frac. Radiant',
  'Fan Schedule',
  'Setpoint Schedule',
] as const;

/** Display order for HVAC mismatch tables (one table per field). */
export const HVAC_FIELD_ORDER = [
  'Cool Avail Schedule',
  'Heat Avail Schedule',
  'Cooling Setpoint Schedule',
  'Heating Setpoint Schedule',
  'humidification_control_type',
  'dehumidification_control_type',
  'heat_recovery_type',
  'cooling_limit',
  'heating_limit',
  'outdoor_air_economizer_type',
  'maximum_heating_supply_air_humidity_ratio',
  'maximum_heating_supply_air_temperature',
  'minimum_cooling_supply_air_humidity_ratio',
  'minimum_cooling_supply_air_temperature',
] as const;

export const PUMP_SECTION = 'Pump';
export const PUMP_SECTION_LABEL = 'Pump / Ceiling Fan';
export const HVAC_SECTION = 'HVAC';

/** Normalize field labels so "Watt/ Area" (from formatKey) matches "Watt/Area". */
export function normalizePumpField(field: string): string {
  return (field || '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\s*\/\s*/g, '/');
}

export function isPumpSection(section: string): boolean {
  return section === PUMP_SECTION;
}

export function isHvacSection(section: string): boolean {
  return section === HVAC_SECTION;
}

export const PEOPLE_SECTION = 'People';

/** Comparison sections on the Equipments checklist, in display order. */
export const EQUIPMENT_GAIN_SECTIONS = [
  'Computing Gain',
  'Equipment Gain',
  'Miscellaneous Gain',
  'Catering Gain',
  'Process Gain',
] as const;

export function isEquipmentGainSection(section: string): boolean {
  return (EQUIPMENT_GAIN_SECTIONS as readonly string[]).includes(section);
}

/**
 * Equipment sections become one block, in EQUIPMENT_GAIN_SECTIONS order, at the
 * first equipment slot. Every other section stays in its original order.
 */
export function orderEquipmentSections<T>(entries: readonly T[], sectionOf: (entry: T) => string): T[] {
  const rank = new Map<string, number>(EQUIPMENT_GAIN_SECTIONS.map((name, index) => [name, index]));
  const before: T[] = [];
  const equipment: T[] = [];
  const after: T[] = [];
  let seenEquipment = false;
  for (const entry of entries) {
    if (rank.has(sectionOf(entry))) {
      equipment.push(entry);
      seenEquipment = true;
    } else if (!seenEquipment) {
      before.push(entry);
    } else {
      after.push(entry);
    }
  }
  equipment.sort((a, b) => rank.get(sectionOf(a))! - rank.get(sectionOf(b))!);
  return [...before, ...equipment, ...after];
}

/** Sections that always use Unit | Zone columns (Pump, HVAC, People). */
export function isUnitZoneGroupedSection(section: string): boolean {
  return isPumpSection(section) || isHvacSection(section) || section === PEOPLE_SECTION;
}

/**
 * True when every non-empty zone token is `unit:zone` (e.g. 00X01:LIVING).
 * Empty tokens are ignored. A single label without `:` (Global, a material name)
 * keeps the plain Zone | Field table.
 */
export function zonesUseUnitPrefix(zoneTexts: Array<string | undefined | null>): boolean {
  const tokens = zoneTexts.flatMap((zoneText) => splitPumpZoneList(zoneText || ''));
  if (tokens.length === 0) return false;
  return tokens.every((token) => {
    const colonIdx = token.indexOf(':');
    return colonIdx > 0 && colonIdx < token.length - 1;
  });
}

/** Pump, HVAC, and People always. Number of People / People/Area list every zone of the unit (`00X01:LIVING, 00X01:BED`). Any other section whose zones are all `unit:zone`. */
export function shouldUseUnitZoneGrouping(
  section: string,
  zoneTexts: Array<string | undefined | null>
): boolean {
  return isUnitZoneGroupedSection(section) || zonesUseUnitPrefix(zoneTexts);
}

/** Split "00X01:LIVING" into unit + zone name (same convention as unit-areas BlockTable). */
export function parsePumpZone(fullZone: string): { unit: string; zoneName: string } {
  const trimmed = (fullZone || '').trim();
  if (!trimmed) return { unit: '', zoneName: '' };
  const colonIdx = trimmed.indexOf(':');
  if (colonIdx === -1) return { unit: trimmed, zoneName: '-' };
  return {
    unit: trimmed.slice(0, colonIdx),
    zoneName: trimmed.slice(colonIdx + 1),
  };
}

export function splitPumpZoneList(zonesText: string): string[] {
  if (!zonesText?.trim()) return [];
  return zonesText.split(',').map((z) => z.trim()).filter(Boolean);
}

export type PumpAggregatedUnitRow = { unit: string; zoneDisplay: string; rowKey: string };

function groupZoneNamesByUnit(fullZones: string[]): Map<string, string[]> {
  const byUnit = new Map<string, string[]>();
  fullZones.forEach((fullZone) => {
    const { unit, zoneName } = parsePumpZone(fullZone);
    const unitKey = unit || 'Global';
    if (!byUnit.has(unitKey)) byUnit.set(unitKey, []);
    if (zoneName) byUnit.get(unitKey)!.push(zoneName);
  });
  return byUnit;
}

/**
 * Within a mismatch row (same revision value), group zones by unit.
 * e.g. 00X01:LIVING + 00X01:MAMAD + 00X01:SERVICE → one row: 00X01 | LIVING, MAMAD, SERVICE
 */
export function aggregatePumpMismatchByUnit<T extends { zone?: string; revisionValue?: string }>(
  mismatch: T
): Array<T & PumpAggregatedUnitRow> {
  const byUnit = groupZoneNamesByUnit(splitPumpZoneList(mismatch.zone || ''));
  if (byUnit.size === 0) {
    return [{ ...mismatch, unit: 'Global', zoneDisplay: '-', rowKey: 'global' }];
  }
  return Array.from(byUnit.entries()).map(([unit, zoneNames]) => ({
    ...mismatch,
    unit,
    zoneDisplay: zoneNames.join(', '),
    rowKey: `${unit}|||${mismatch.revisionValue ?? ''}|||${zoneNames.join(',')}`,
  }));
}

/** Same as aggregatePumpMismatchByUnit but for grouped section rows (zones: string[]). */
export function aggregatePumpSectionGroupsByUnit<T extends { zones?: string[]; displayValue?: string }>(
  groups: T[]
): Array<T & PumpAggregatedUnitRow & { groupIndex: number }> {
  return groups.flatMap((group, groupIndex) => {
    const fullZones = (group.zones || []).flatMap((zoneEntry) => splitPumpZoneList(zoneEntry));
    const byUnit = groupZoneNamesByUnit(fullZones);
    if (byUnit.size === 0) {
      return [{
        ...group,
        groupIndex,
        unit: 'Global',
        zoneDisplay: '-',
        rowKey: `global-${groupIndex}`,
      }];
    }
    return Array.from(byUnit.entries()).map(([unit, zoneNames]) => ({
      ...group,
      groupIndex,
      unit,
      zoneDisplay: zoneNames.join(', '),
      rowKey: `${groupIndex}-${unit}|||${group.displayValue ?? ''}`,
    }));
  });
}

export function sortPumpUnitRows<T extends { unit: string; zoneDisplay: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const unitCmp = a.unit.localeCompare(b.unit, undefined, { sensitivity: 'base', numeric: true });
    if (unitCmp !== 0) return unitCmp;
    return a.zoneDisplay.localeCompare(b.zoneDisplay, undefined, { sensitivity: 'base', numeric: true });
  });
}

export function isPumpWattField(field: string | undefined): boolean {
  return normalizePumpField(field || '') === 'Watt';
}

function parseNumericDisplayValue(value: string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const n = parseFloat(String(value).replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : null;
}

function countZonesInDisplay(zoneDisplay: string | undefined): number {
  if (!zoneDisplay?.trim() || zoneDisplay.trim() === '-') return 1;
  const parts = zoneDisplay.split(',').map((z) => z.trim()).filter(Boolean);
  return Math.max(parts.length, 1);
}

/**
 * When a unit filter is active, collapse Pump Watt rows for that unit into one row:
 * revision (and numeric expected) = sum of (value × zone count) across rows.
 * Zone display lists all zones. No-op if filter is empty or field is not Watt.
 *
 * Zone-count weighting matters because the API already merges zones that share the same
 * Watt into one mismatch row (e.g. LIVING+MAMAD at 50 W → contribution 100 W).
 */
export type PumpWattZoneSumMeta = {
  /** True when this row is the unit-filtered Watt total across zones. */
  isWattZoneSum?: boolean;
  /** Zone names included in the Watt sum (for tooltips). */
  wattSumZoneNames?: string[];
};

export function sumPumpWattRowsWhenUnitFiltered<
  T extends {
    unit: string;
    zoneDisplay: string;
    rowKey: string;
    field?: string;
    revisionValue?: string;
    codeValue?: string | null;
    isRed?: boolean;
    severityType?: 'null' | 'orange' | 'red' | 'match' | 'missing';
    hasNullCodeValue?: boolean;
  }
>(rows: T[], selectedUnit: string, field: string | undefined): Array<T & PumpWattZoneSumMeta> {
  if (!selectedUnit || !isPumpWattField(field) || rows.length === 0) return rows;

  const zoneNames: string[] = [];
  let revisionSum = 0;
  let codeSum = 0;
  let hasNumericCode = false;
  let sawNonNumericRevision = false;
  let anyRed = false;
  let anyNullCode = false;
  let worstSeverity: 'null' | 'orange' | 'red' | 'match' | 'missing' | undefined;

  rows.forEach((row) => {
    const zoneCount = countZonesInDisplay(row.zoneDisplay);
    if (row.zoneDisplay?.trim() && row.zoneDisplay.trim() !== '-') {
      row.zoneDisplay.split(',').map((z) => z.trim()).filter(Boolean).forEach((z) => {
        if (!zoneNames.includes(z)) zoneNames.push(z);
      });
    }
    const rev = parseNumericDisplayValue(row.revisionValue);
    if (rev == null) sawNonNumericRevision = true;
    else revisionSum += rev * zoneCount;

    const code = parseNumericDisplayValue(
      row.codeValue == null ? null : String(row.codeValue)
    );
    if (code != null) {
      hasNumericCode = true;
      codeSum += code * zoneCount;
    }

    if (row.isRed) anyRed = true;
    if (row.hasNullCodeValue) anyNullCode = true;
    if (row.severityType === 'red') worstSeverity = 'red';
    else if (row.severityType === 'orange' && worstSeverity !== 'red') worstSeverity = 'orange';
    else if (row.severityType && !worstSeverity) worstSeverity = row.severityType;
  });

  if (sawNonNumericRevision) return rows;

  const base = rows[0];
  return [{
    ...base,
    unit: selectedUnit,
    zoneDisplay: zoneNames.length ? zoneNames.join(', ') : (base.zoneDisplay || '-'),
    revisionValue: String(revisionSum),
    codeValue: hasNumericCode ? String(codeSum) : base.codeValue,
    isRed: anyRed,
    hasNullCodeValue: anyNullCode,
    severityType: anyRed ? 'red' : (worstSeverity ?? base.severityType),
    rowKey: `${selectedUnit}|||watt-sum|||${zoneNames.join(',')}`,
    isWattZoneSum: true,
    wattSumZoneNames: zoneNames,
  }];
}

export function collectPumpUnitsFromZoneLists(zoneLists: string[][]): string[] {
  const units = new Set<string>();
  zoneLists.forEach((zones) => {
    zones.forEach((fullZone) => {
      const { unit } = parsePumpZone(fullZone);
      if (unit) units.add(unit);
    });
  });
  return Array.from(units).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

export function collectPumpUnitsFromMismatchZones(mismatches: { zone?: string }[]): string[] {
  return collectPumpUnitsFromZoneLists(mismatches.map((m) => splitPumpZoneList(m.zone || '')));
}

export function collectPumpUnitsFromSectionGroups(groups: { zones?: string[] }[]): string[] {
  return collectPumpUnitsFromZoneLists(groups.map((g) => g.zones || []));
}

export function filterPumpRowsByUnit<T extends { unit: string }>(
  rows: T[],
  selectedUnit: string
): T[] {
  if (!selectedUnit) return rows;
  return rows.filter((row) => row.unit === selectedUnit);
}

/** Unit-zone table view: one row per unit (default) or collapse identical revision values. */
export type UnitZoneTableView = 'byUnit' | 'byRevisionValue';

function revisionGroupKey(
  revisionValue: string | undefined,
  codeValue: string | null | undefined,
  displayValue?: string
): string {
  const rev = revisionValue ?? displayValue ?? '';
  return `${rev}|||${codeValue == null ? '' : String(codeValue)}`;
}

function uniqueSortedJoin(parts: string[]): string {
  const seen = new Set<string>();
  const out: string[] = [];
  parts.forEach((p) => {
    const t = (p || '').trim();
    if (!t || t === '-' || seen.has(t)) return;
    seen.add(t);
    out.push(t);
  });
  out.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }));
  return out.join(', ') || '-';
}

/**
 * Collapse unit-rows that share the same revision (+ expected) value.
 * Unit cell lists all units; Zone cell lists unique zone names across the group.
 * Accepts either `revisionValue` (ReportViewer) or `displayValue` (EnergyPlusViewer).
 */
export function groupPumpRowsByRevisionValue<
  T extends {
    unit: string;
    zoneDisplay: string;
    rowKey: string;
    revisionValue?: string;
    displayValue?: string;
    codeValue?: string | null;
    zones?: string[];
  }
>(rows: T[]): T[] {
  if (rows.length <= 1) return rows;

  const groups = new Map<string, T[]>();
  rows.forEach((row) => {
    const key = revisionGroupKey(row.revisionValue, row.codeValue, row.displayValue);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(row);
  });

  return Array.from(groups.values()).map((groupRows) => {
    if (groupRows.length === 1) return groupRows[0];
    const units = uniqueSortedJoin(groupRows.map((r) => r.unit));
    const zones = uniqueSortedJoin(
      groupRows.flatMap((r) =>
        (r.zoneDisplay || '')
          .split(',')
          .map((z) => z.trim())
          .filter(Boolean)
      )
    );
    const mergedZones = Array.from(
      new Set(groupRows.flatMap((r) => r.zones || []).map((z) => z.trim()).filter(Boolean))
    );
    const base = groupRows[0];
    return {
      ...base,
      unit: units,
      zoneDisplay: zones,
      ...(mergedZones.length ? { zones: mergedZones } : {}),
      rowKey: `rev|||${revisionGroupKey(base.revisionValue, base.codeValue, base.displayValue)}|||${units}`,
    };
  });
}

/** Apply unit filter then optional revision-value grouping for Pump/HVAC tables. */
export function prepareUnitZoneTableRows<
  T extends {
    unit: string;
    zoneDisplay: string;
    rowKey: string;
    revisionValue?: string;
    displayValue?: string;
    codeValue?: string | null;
  }
>(
  aggregatedRows: T[],
  selectedUnit: string,
  view: UnitZoneTableView = 'byRevisionValue'
): T[] {
  const filtered = filterPumpRowsByUnit(aggregatedRows, selectedUnit);
  if (view === 'byRevisionValue') return groupPumpRowsByRevisionValue(filtered);
  return filtered;
}

/** Group items by field, returning fields in `fieldOrder` then any extras alphabetically. */
export function groupByOrderedField<T extends { field?: string }>(
  items: T[],
  fieldOrder: readonly string[]
): Array<[string, T[]]> {
  const byField = new Map<string, T[]>();
  items.forEach((item) => {
    const field = normalizePumpField(item.field || 'Other');
    if (!byField.has(field)) byField.set(field, []);
    byField.get(field)!.push(item);
  });

  const ordered: Array<[string, T[]]> = [];
  for (const field of fieldOrder) {
    const group = byField.get(normalizePumpField(field));
    if (group?.length) ordered.push([field, group]);
    byField.delete(normalizePumpField(field));
  }
  Array.from(byField.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .forEach(([field, group]) => ordered.push([field, group]));

  return ordered;
}

/** Group items by field, returning fields in PUMP_FIELD_ORDER then any extras alphabetically. */
export function groupByPumpField<T extends { field?: string }>(items: T[]): Array<[string, T[]]> {
  return groupByOrderedField(items, PUMP_FIELD_ORDER);
}

/** Group items by field, returning fields in HVAC_FIELD_ORDER then any extras alphabetically. */
export function groupByHvacField<T extends { field?: string }>(items: T[]): Array<[string, T[]]> {
  return groupByOrderedField(items, HVAC_FIELD_ORDER);
}

/** Keep the order fields first appear in, for sections without a fixed field list. */
function groupByEncounteredField<T extends { field?: string }>(items: T[]): Array<[string, T[]]> {
  const byField = new Map<string, T[]>();
  const order: string[] = [];
  items.forEach((item) => {
    const field = normalizePumpField(item.field || 'Other');
    if (!byField.has(field)) {
      byField.set(field, []);
      order.push(field);
    }
    byField.get(field)!.push(item);
  });
  return order.map((field) => [field, byField.get(field)!]);
}

/** Field groups for unit-zone sections. Pump and HVAC keep their fixed order. */
export function groupByUnitZoneSectionField<T extends { field?: string }>(
  section: string,
  items: T[]
): Array<[string, T[]]> {
  if (isHvacSection(section)) return groupByHvacField(items);
  if (isPumpSection(section)) return groupByPumpField(items);
  return groupByEncounteredField(items);
}
